import { GRAZE } from '../../../config/constants.js';

/**
 * Graze chain (BULLET-HELL.md §D5).
 *
 * Graze is Survivor's best-designed mechanic and, until this, its most
 * invisible one: it paid 0.012 of an ultimate bar and 0.15 Glimmer that the
 * player first saw on the run-end screen. The genre's rule is the opposite —
 * risk has to be legible and paid on the spot — so grazes now chain. Every
 * near-miss refreshes a `GRAZE.chainWindow` timer and raises a multiplier that
 * prices the next one, and the whole thing is on screen at the player.
 *
 * Deliberately a pure listener with no reach into anything: it counts and
 * decays, and exposes `multiplier` / `magnetBonus` for the three systems that
 * spend it (UltimateSystem's charge, PickupSystem's magnet radius, and the
 * run-end Glimmer via `value`). Like CaptureTracker, that means one instance
 * covers every SpawningSystem in the run — both bosses and both heralds —
 * without knowing more than one exists.
 *
 * Taking a hit BREAKS the chain. §D5 originally spared it — capture (§D1)
 * already prices getting hit, and breaking both looked like a pile-on — but in
 * play that read as the meter climbing *while* the player was being hit, which
 * is the one thing a risk readout must never say. The chain claims you stood
 * next to the bullets and got away with it; a hit is the proof you didn't.
 */
export class GrazeChain {
  constructor(eventBus) {
    this.eventBus = eventBus;
    /** Grazes in the chain currently running; 0 when it has lapsed. */
    this.count = 0;
    /** Seconds left before the chain lapses. */
    this.remaining = 0;
    /** Longest chain this run — a run stat, surfaced at the end screen. */
    this.peak = 0;
    /**
     * Sum of the multipliers every graze this run was worth. Run-end Glimmer
     * is paid on this rather than on the raw graze count (Currency.calcGlimmer's
     * `grazeValue`), so a run spent threading curtains out-earns a run spent
     * drifting past one bullet at a time even at identical graze totals.
     */
    this.value = 0;
    // Seconds of readout still owed after a chain lapses (GRAZE.readoutFade),
    // so the final number is readable instead of blinking out.
    this.fade = 0;

    eventBus.on('bullet:grazed', () => this.add());
    eventBus.on('player:damaged', () => this.breakChain());
  }

  /**
   * Ends a running chain on a hit, but leaves the readout fading rather than
   * blanking it: the number the player lost has to be legible for the break to
   * teach anything. `reset()` stays the silent, immediate clear for stage and
   * run boundaries, where there is no player to inform.
   */
  breakChain() {
    if (this.count === 0 || this.remaining <= 0) return;
    this.remaining = 0;
    this.fade = GRAZE.readoutFade;
    this.eventBus.emit('graze:chain-broken', { count: this.count });
  }

  /** 1 .. GRAZE.multiplierMax — what the CURRENT graze is worth. */
  get multiplier() {
    return Math.min(1 + this.count * GRAZE.multiplierPerGraze, GRAZE.multiplierMax);
  }

  /** 0..1 of the window left, for the decay bar under the readout. */
  get fraction() {
    return GRAZE.chainWindow > 0 ? Math.max(0, this.remaining) / GRAZE.chainWindow : 0;
  }

  /** Extra mote magnet radius the chain is currently worth, in px. */
  get magnetBonus() {
    if (this.count === 0) return 0;
    const span = GRAZE.multiplierMax - 1;
    if (span <= 0) return 0;
    return ((this.multiplier - 1) / span) * GRAZE.magnetBonusAtMax;
  }

  /** Whether there is anything to draw (a live chain, or one still fading). */
  get isVisible() {
    return this.count > 0 && (this.remaining > 0 || this.fade > 0);
  }

  /**
   * Counts one graze. The multiplier is computed AFTER the increment, so the
   * chain you are standing in is what prices the graze that extends it — the
   * alternative (pay first, then count) makes the readout on screen disagree
   * with what the last graze was actually worth.
   */
  add() {
    this.count++;
    this.remaining = GRAZE.chainWindow;
    this.fade = 0;
    if (this.count > this.peak) this.peak = this.count;
    const multiplier = this.multiplier;
    this.value += multiplier;
    this.eventBus.emit('graze:chained', { count: this.count, multiplier });
  }

  update(dt) {
    if (dt <= 0) return;
    if (this.remaining > 0) {
      this.remaining -= dt;
      if (this.remaining > 0) return;
      // Whatever the step overshot the window by spills into the fade, so one
      // long tick (a stall, a tab restored) ages the readout the same amount
      // as the frames it stood in for.
      dt = -this.remaining;
      this.remaining = 0;
      this.fade = GRAZE.readoutFade;
      this.eventBus.emit('graze:chain-broken', { count: this.count });
    }
    if (this.fade > 0) {
      this.fade -= dt;
      // The count survives the fade only so the readout can show what it
      // ended on; it is cleared here, once nothing is looking at it.
      if (this.fade <= 0) {
        this.fade = 0;
        this.count = 0;
      }
    }
  }

  /** Ends the chain outright — a stage boundary, a death, a run end. */
  reset() {
    this.count = 0;
    this.remaining = 0;
    this.fade = 0;
  }
}
